import type React from "react"
import { Col, Row } from "reactstrap"
import type { DashboardStats, RealTimeStats } from "./types/dashboard"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface SummaryCardsProps {
  stats: DashboardStats | null
  realTimeStats: RealTimeStats | null
  loading: boolean
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ stats, realTimeStats, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "FRW",
    }).format(amount)
  }

  const totalRevenue =
    (stats?.extraBadgeAmount?.totalAmount || 0) +
    (stats?.ticketStats.totalAmount || 0) +
    (stats?.vehicleStats.totalAmount || 0)

  if (loading) {
    return (
      <Row>
        {Array.from({ length: 3 }).map((_, index) => (
          <Col xl="4" lg="6" md="6" key={index} className="mb-4">
            <div className="db-card" style={{ padding: "1.25rem" }}>
              <div className="db-skeleton mb-3" style={{ height: "22px", width: "50%" }}></div>
              <div className="db-skeleton" style={{ height: "110px", width: "100%" }}></div>
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  const SummaryHeader = ({ icon, title, accent }: { icon: string; title: string; accent: string }) => (
    <div className="d-flex align-items-center mb-3">
      <div className={`db-kpi-chip db-accent-${accent} me-2`} style={{ width: 36, height: 36 }}>
        <i className={`fa ${icon}`} style={{ fontSize: "0.9rem" }}></i>
      </div>
      <h3 className={`db-text-${accent} mb-0`} style={{ fontSize: "1.05rem", fontWeight: 700 }}>
        {title}
      </h3>
    </div>
  )

  const Row2 = ({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) => (
    <div className="d-flex justify-content-between align-items-center py-1">
      <span className="text-muted" style={{ fontSize: "0.88rem" }}>
        {label}
      </span>
      <span className={bold ? "fw-bold" : "fw-semibold"}>{value}</span>
    </div>
  )

  return (
    <Row>
      <Col xl="4" lg="6" md="6" className="mb-4">
        <div className="db-kpi-card">
          <SummaryHeader icon="fa-line-chart" title="Revenue" accent="emerald" />
          <div>
            <Row2 label="Badge Rev:" value={formatCurrency(stats?.extraBadgeAmount?.totalAmount || 0)} />
            <Row2 label="Ticket Rev:" value={formatCurrency(stats?.ticketStats.totalAmount || 0)} />
            <Row2 label="Vehicle Rev:" value={formatCurrency(stats?.vehicleStats.totalAmount || 0)} />
            <hr className="db-divider" style={{ margin: "0.5rem 0" }} />
            <div className="d-flex justify-content-between align-items-center py-1">
              <span className="fw-bold">Total:</span>
              <span className="db-text-emerald" style={{ fontSize: "1.15rem", fontWeight: 800 }}>
                {formatCurrency(totalRevenue)}
              </span>
            </div>
          </div>
        </div>
      </Col>

      <Col xl="4" lg="6" md="6" className="mb-4">
        <div className="db-kpi-card">
          <SummaryHeader icon="fa-id-badge" title="Badge Distribution" accent="amber" />
          <div>
            {stats?.exhibitorBadgeStats?.exhibitor_badge_stats?.length ? (
              stats.exhibitorBadgeStats.exhibitor_badge_stats.map((item, index) => (
                <Row2
                  key={index}
                  label={`${item.exhibitor_type}:`}
                  value={
                    <span className="db-text-indigo">
                      {item.badge_count} badge{item.badge_count !== 1 ? "s" : ""}
                    </span>
                  }
                />
              ))
            ) : (
              <div className="db-empty">
                <i className="fa fa-info-circle me-2"></i>
                No badge distribution data available
              </div>
            )}
            {stats?.exhibitorBadgeStats?.total_badges && (
              <>
                <hr className="db-divider" style={{ margin: "0.5rem 0" }} />
                <Row2
                  label="Total Distributed:"
                  bold
                  value={<span className="db-text-indigo">{stats.exhibitorBadgeStats.total_badges} badges</span>}
                />
              </>
            )}
          </div>
        </div>
      </Col>

      <Col xl="4" lg="6" md="6" className="mb-4">
        <div className="db-kpi-card">
          <SummaryHeader icon="fa-truck" title="Vehicle Status" accent="cyan" />
          <div>
            <Row2 label="Check-ins:" value={stats?.vehicleStats.totalVisits || 0} />
            <Row2
              label="Still in Parking:"
              value={<span className="db-text-emerald">{stats?.vehicleStats.activeVisitors || 0}</span>}
            />
            <Row2
              label="Today's Check-ins:"
              value={<span className="db-text-indigo">{realTimeStats?.todayEntries || 0}</span>}
            />
            <Row2
              label="Today's Check Out:"
              value={<span className="db-text-amber">{realTimeStats?.todayExits || 0}</span>}
            />
          </div>
        </div>
      </Col>
    </Row>
  )
}

export default SummaryCards
